<template>
	<div
		class="app-date-string-picker"
		v-click-outside="handleClickOutside"
	>
		<div
			ref="triggerRef"
			class="app-date-string-picker__trigger"
			@click="openPicker"
		>
			<slot
				name="trigger"
				:date="selectedDate"
				:formatted="formattedDate"
				:open="openPicker"
			>
				<div
					:class="{
						'app-date-string-picker__default-trigger': true,
						'app-date-string-picker__default-trigger--date-selected': !!selectedDate,
					}"
				>
					<app-icon
						class="app-date-string-picker__default-trigger-icon"
						name="clock"
						width="18px"
						color="#979797"
					/>
					<div class="app-date-string-picker__default-trigger-date">
						{{ formattedDate || placeholder || $t("ui_date_placeholder") }}
					</div>
				</div>
			</slot>
		</div>
		<div
			v-if="isOpen"
			class="app-date-string-picker__dropdown"
			:style="dropdownStyles"
		>
			<VueDatePicker
				ref="pickerRef"
				v-model="selectedDate"
				:format="format"
				:locale="locale"
				:enable-time-picker="false"
				:min-date="minDate"
				:max-date="maxDate"
				:auto-apply="autoApply"
				@update:model-value="handleDateSelect"
				@open="onPickerOpen"
				@close="onPickerClose"
				:teleport="false"
				:inline="true"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	computed,
	watch
} from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";

type Props = {
	modelValue?: Date | null
	format?: string
	locale?: string
	minDate?: Date
	maxDate?: Date
	autoApply?: boolean
	placeholder?: string
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: null,
	format: 'dd.MM.yyyy',
	locale: 'ru',
	minDate: undefined,
	maxDate: undefined,
	autoApply: true,
	placeholder: ''
})

const emit = defineEmits<{
	(e: 'update:modelValue', value: Date | null): void
	(e: 'select', value: Date | null): void
	(e: 'open'): void
	(e: 'close'): void
}>()

const selectedDate = ref<Date | null>(props.modelValue)
const isOpen = ref(false)
const triggerRef = ref<HTMLElement | null>(null)

const formattedDate = computed(() => {
	if (!selectedDate.value) return null
	return selectedDate.value.toLocaleDateString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	})
})

const minDate = computed(() => {
	if (props.minDate) return props.minDate
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	return today
})

const dropdownStyles = computed(() => {
	if (!triggerRef.value) return {}

	const rect = triggerRef.value.getBoundingClientRect()
	return {
		position: 'fixed',
		top: rect.bottom + 4 + 'px',
		left: rect.left + 'px',
		zIndex: 9999
	}
})

const openPicker = () => {
	isOpen.value = true
	emit('open')
}

const handleDateSelect = (date: Date | null) => {
	selectedDate.value = date
	emit('update:modelValue', date)
	emit('select', date)
	isOpen.value = false
	emit('close')
}

const onPickerOpen = () => {
}

const onPickerClose = () => {
	isOpen.value = false
	emit('close')
}

const handleClickOutside = () => {
	isOpen.value = false
	emit('close')
}

watch(() => props.modelValue, (newVal) => {
	selectedDate.value = newVal
})
</script>

<style lang="scss">
.app-date-string-picker {
	display: inline-block;

	&__trigger {
		cursor: pointer;
	}

	&__default-trigger {
		display: flex;
		align-items: center;
		gap: 4px;
		font-size: 14px;
		line-height: 18px;
		font-weight: 400;
		color: #4D4D4D;

		&--date-selected {
			color: #3F884F;
			
			.app-date-string-picker__default-trigger-icon {
				color: #3F884F !important;
			}
		}

		&-icon {
			margin-top: -2px;
		}
	}

	&__icon {
		font-size: 16px;
	}

	&__dropdown {
		position: fixed;
		z-index: 9999;

		:deep(.dp__main) {
			width: auto;
		}

		:deep(.dp__menu) {
			position: relative !important;
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
			border-radius: 4px;
		}

		:deep(.dp__input_wrap) {
			display: none !important;
		}

		:deep(.dp__input) {
			display: none !important;
		}
	}
}
</style>
